import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertBar } from '@/components/AlertBar';
import { AnimatePresence } from 'motion/react';
import { Timer1 } from 'iconsax-reactjs';
import { timeframeShort, TIMEFRAME_SECONDS, type Timeframe } from '@pipyar/core';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { useAlertSound } from '@/lib/useAlertSound';
import { useKnotSummary, useSummaryClock, type PendingKnot } from './summary';
import { formatBrokerTime } from '@/lib/time';

/**
 * How long a pending take stays on screen.
 *
 * A pending is news: a close has landed on a knot and the NEXT candle decides
 * it. How long that stays worth a banner is not how long the decision takes —
 * on W1 the answer arrives a week later, and a bar pinned to the top of every
 * screen for a week is wallpaper, not an alert.
 *
 * So the bar shows for the length of the deciding candle, capped. On M15 the
 * cap never applies and the alert stands until that candle closes, which is
 * exactly right; on D1 and W1 it gives half an hour of notice and then gets
 * out of the way. The knot stays on the Knots page and the dashboard tile the
 * whole time — only the banner expires.
 */
const LINGER_CAP_SEC = 30 * 60;

/**
 * Age is measured from the data, not from when this browser first saw it.
 *
 * Measuring locally would restart the clock on every reload, so a week-old W1
 * pending would reappear for another half hour each time the page was opened.
 * The close that created the pending is a fixed instant, so deriving the age
 * from it gives the same answer on every device and across reloads.
 */
function isFresh(pendingAt: number, timeframe: Timeframe, nowUtc: number): boolean {
  const bar = TIMEFRAME_SECONDS[timeframe];
  // pendingAt is the open of the taking candle, so its close is one bar later:
  // that is the moment the pending became knowable.
  return nowUtc - (pendingAt + bar) < Math.min(bar, LINGER_CAP_SEC);
}

interface Shown {
  key: string;
  timeframe: Timeframe;
  pending: PendingKnot;
}

/**
 * Announces a close landing on a knot, before the next candle has confirmed it.
 *
 * The early half of the knot alert: this one says "a take is forming, watch
 * the next candle"; KnotAlert says it counted. The card shows which colour the
 * next candle must close, and goes away by itself the moment the summary stops
 * reporting it — confirmed (KnotAlert takes over) or dropped as invalid.
 */
export function PendingKnotAlert() {
  const { symbol, instrument } = useSymbol();
  const query = useKnotSummary(symbol);
  const { t } = useI18n();
  const { play } = useAlertSound('knotPending');
  const playRef = useRef(play);
  playRef.current = play;

  /** Every pending take currently reported, newest first. */
  const current = useMemo<Shown[]>(() => {
    const rows = query.data?.timeframes ?? [];
    return rows
      .filter((r) => r.pending !== null)
      .map((r) => ({
        key: `${r.timeframe}:${r.pending!.pendingAt}:${r.pending!.level}`,
        timeframe: r.timeframe,
        pending: r.pending!,
      }))
      .sort((a, b) => b.pending.pendingAt - a.pending.pendingAt);
  }, [query.data]);

  /*
   * Keys already seen. Null until the first answer, which only seeds it — a
   * take that was already pending when the page loaded is shown, but does not
   * make a sound, the same way KnotAlert does not announce the backlog.
   */
  const seen = useRef<Set<string> | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!query.data) return;
    const keys = current.map((c) => c.key);
    if (seen.current === null) {
      seen.current = new Set(keys);
      return;
    }
    const fresh = keys.filter((k) => !seen.current!.has(k));
    for (const k of keys) seen.current.add(k);
    if (fresh.length > 0) playRef.current();
  }, [current, query.data]);

  /*
   * Freshness is applied here rather than to `current` above, which the sound
   * effect reads: that one has to see every pending the endpoint reports, so a
   * take already standing at load time is recorded as seen and does not ring.
   */
  const { nowUtc, trusted } = useSummaryClock(query);
  const shown = !trusted
    ? []
    : current
        .filter((c) => !dismissed.has(c.key) && isFresh(c.pending.pendingAt, c.timeframe, nowUtc))
        .slice(0, 2);
  const digits = query.data?.digits ?? 2;

  return (
    <AnimatePresence>
      {shown.map((s) => {
        const needsBull = s.pending.needs === 'up';
        return (
          <AlertBar
            key={s.key}
            accent="#0ea5e9"
            icon={<Timer1 size={16} variant="Bold" />}
            kind={t('knots.pending')}
            title={t('knots.alertPending', { timeframe: timeframeShort(s.timeframe) })}
            subtitle={
              <>
                <span className={needsBull ? 'text-bull' : 'text-bear'}>
                  {t(needsBull ? 'knots.pendingNeedsBull' : 'knots.pendingNeedsBear')}
                </span>
                {' · '}
                {/* A true UTC instant, like takenAt — see KnotAlert. */}
                <span className="tabular" title={t('common.brokerTime')}>
                  {formatBrokerTime(
                    s.pending.pendingAt,
                    instrument?.storedGmtOffsetSec ?? 0,
                    t('common.today'),
                  )}
                </span>
              </>
            }
            valueCaption={t('knots.levelCaption')}
            value={s.pending.level.toFixed(digits)}
            onClose={() => setDismissed((prev) => new Set(prev).add(s.key))}
            closeLabel={t('knots.dismissPending')}
          />
        );
      })}
    </AnimatePresence>
  );
}
